import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import UserService from "../../service/user";
import { setUser, showToast } from "../../store/actions";
import { ButtonUI, PasswordValidationText } from "../Common/Common";
import CommonLayout from "../Common/Layout";
import { regexEmail, regexPassword } from "../../util/regularExpress";

const useStyles = makeStyles({
  Header: {
    fontSize: "1.1rem",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    display: "inline-block",
  },
  subContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderTop: 0,
  },
  PanelContainer: {
    padding: "5px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  Panel: {
    padding: "25px",
    backgroundColor: "#FFFFFF",
    marginTop: 0,
    borderRadius: 10,
    boxShadow: "none",
  },
  TileInput: {
    margin: "10px 0",
  },
  credentialsInputLabel: {
    fontWeight: "bold",
    whiteSpace: "nowrap",
    display: "inline-block",
    minWidth: "70px",
  },
});

function MyProfile() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);
  const role = user.currentUser?.Role_id;

  const [loading, setLoading] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [passwords, setPasswords] = useState({
    currPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    currPassword: false,
    newPassword: false,
    confirmPassword: false,
    email: false,
  });

  const handlePersonalInfoChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;

    setPersonalInfo((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;

    setPasswords((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  useEffect(() => {
    const loadPersonalInfo = () => {
      const { currentUser } = user;
      const { FirstName, LastName, Email } = currentUser;

      setPersonalInfo({
        firstName: FirstName,
        lastName: LastName,
        email: Email,
      });
    };
    loadPersonalInfo();
  }, [user]);

  const handleShowPassword = (e, name) => {
    setShowPassword((prevState) => {
      return {
        ...prevState,
        [name]: !prevState[name],
      };
    });
  };

  const handleUpdatePersonalInfo = async () => {
    const { firstName, lastName, email } = personalInfo;

    setErrors((prevState) => {
      return {
        ...prevState,
        firstName: firstName.trim().length < 3 || firstName.trim().length > 50,
        lastName: lastName.trim().length < 3 || lastName.trim().length > 50,
        email: email.trim().length > 100 || !regexEmail(email.trim())
      };
    });

    if (
      !(
        firstName.trim().length < 3 ||
        firstName.trim().length > 50 ||
        lastName.trim().length < 3 ||
        lastName.trim().length > 50 ||
        email.trim().length > 100 || 
        !regexEmail(email.trim())
      )
    ) {
      setLoading(true);

      const {
        currentUser: { User_id },
      } = user;

      let submitData = {
        FirstName: firstName.trim(),
        LastName: lastName.trim(),
        Email: email.trim(),
      };

      const userService = new UserService();
      let result = null;
      result = await userService.updateUser(User_id, submitData);
      if (result.success) {
        const updatedUser = result.data;
        dispatch(setUser(updatedUser));

        dispatch(
          showToast({
            message: "Personal information updated successfully.",
            type: "success",
          })
        );
      }
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    const { currPassword, newPassword, confirmPassword } = passwords;

    setErrors((prevState) => {
      return {
        ...prevState,
        newPassword: !regexPassword(newPassword.trim()),
        confirmPassword: newPassword.trim() !== confirmPassword.trim(),
      };
    });

    if (
      !(
        !regexPassword(newPassword.trim()) ||
        newPassword.trim() !== confirmPassword.trim()
      )
    ) {
      setLoading(true);

      const {
        currentUser: { User_id },
      } = user;

      let submitData = {
        Password: currPassword.trim(),
        NewPassword: newPassword.trim(),
        ConfirmPassword: confirmPassword.trim(),
      };

      const userService = new UserService();
      let result = null;
      result = await userService.updatePassword(User_id, submitData);

      // If Password is incorrect
      setErrors((prevState) => {
        return {
          ...prevState,
          currPassword: result.message === "Incorrect password.",
        };
      });

      if (result.success) {
        dispatch(
          showToast({
            message: result.message,
            type: "success",
          })
        );
      }

      setPasswords({
        currPassword: "",
        confirmPassword: "",
        newPassword: "",
      });
    }
    setLoading(false);
  };

  const personalInfoForm = () => {
    return (
      <>
        <TextField
          required
          variant="outlined"
          fullWidth
          onChange={handlePersonalInfoChange}
          label="First Name"
          name="firstName"
          className={classes.TileInput}
          value={personalInfo.firstName}
          error={errors.firstName}
          helperText={errors.firstName ? "First Name length should be between 3 and 50" : ""}
        />
        <TextField
          required
          variant="outlined"
          fullWidth
          onChange={handlePersonalInfoChange}
          label="Last Name"
          name="lastName"
          className={classes.TileInput}
          value={personalInfo.lastName}
          error={errors.lastName}
          helperText={errors.lastName ? "Last Name length should be between 3 and 50" : ""}
        />
        <TextField
          type="email"
          required
          variant="outlined"
          fullWidth
          onChange={handlePersonalInfoChange}
          label="Email"
          name="email"
          className={classes.TileInput}
          value={personalInfo.email}
          error={errors.email}
          helperText={errors.email ? "Please enter an valid email." : ""}
        />
      </>
    );
  };

  const updatePasswordForm = () => {
    return (
      <>
        <TextField
          type={showPassword.current ? "text" : "password"}
          required
          variant="outlined"
          fullWidth
          label="Old Password"
          name="currPassword"
          value={passwords.currPassword}
          className={classes.TileInput}
          onChange={handlePasswordChange}
          helperText={errors.currPassword ? "Incorrect Password" : ""}
          error={errors.currPassword}
          placeholder="********"
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={(e) => handleShowPassword(e, "current")}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword.current ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          autoComplete={"off"}
        />
        <TextField
          type={showPassword.new ? "text" : "password"}
          required
          variant="outlined"
          fullWidth
          label="New Password"
          name="newPassword"
          value={passwords.newPassword}
          className={classes.TileInput}
          onChange={handlePasswordChange}
          placeholder="******"
          helperText={errors.newPassword ? <PasswordValidationText /> : ""}
          error={errors.newPassword}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={(e) => handleShowPassword(e, "new")}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword.new ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          autoComplete={"off"}
        />
        <TextField
          type={showPassword.confirm ? "text" : "password"}
          required
          variant="outlined"
          fullWidth
          label="Confirm Password"
          name="confirmPassword"
          value={passwords.confirmPassword}
          className={classes.TileInput}
          onChange={handlePasswordChange}
          helperText={errors.confirmPassword ? "Passwords does not match" : ""}
          placeholder="******"
          error={errors.confirmPassword}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={(e) => handleShowPassword(e, "confirm")}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          autoComplete={"off"}
        />
      </>
    );
  };

  const breadcrumbArr = [
    { link: (user.currentUser && (user.currentUser.isAdminOrEmp === 1 || role === 4)) ? "/admin/dashboard" : "/dashboard", displayName: "Dashboard", active: false },
    { link: "#", displayName: "My Profile", active: true },
  ];

  return (
    <CommonLayout title="My Profile" breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="primary" />
          </Backdrop>
        ) : (
          <Box maxWidth="lg" id = "my-profile-form" className={classes.subContainer}>
            <Grid container direction="column" justifyContent="flex-end" alignItems="left">
              <Grid className={classes.PanelContainer} item>
                <Grid container className={classes.Header} direction="row" alignItems={"center"}>
                  <Grid item xs={12}>
                    <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                      <Typography className={classes.HeaderTitle} component="div">
                        Personal Information
                      </Typography>
                    </Box>
                    <Box sx={{ display: "inline-flex", ml: 2.5 }}>
                      <ButtonUI
                        className="btn btn-dark"
                        variant="contained"
                        disabled={
                          !(
                            personalInfo.firstName.trim() &&
                            personalInfo.lastName.trim() &&
                            personalInfo.email.trim()
                          )
                        }
                        onClick={handleUpdatePersonalInfo}
                      >
                        Update
                      </ButtonUI>
                    </Box>
                  </Grid>
                </Grid>
                <Box className={classes.Panel}>
                  <Grid container direction="row">
                    <Grid item xs={12} sm={10} md={6}>
                      <Box sx={{ padding: "0 0 15px 3px" }}>
                        <span className={classes.credentialsInputLabel}>Login Id: </span> {user.currentUser.UserName}
                      </Box>
                      {personalInfoForm()}
                      <Box sx={{ padding: "10px 0 0 3px" }}>
                        <span className={classes.credentialsInputLabel}>Role:</span>
                        <Box>
                          <FormControlLabel label={user.currentUser.Role} key={user.currentUser.Role} control={<Checkbox checked disabled />} />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Grid
                  container
                  columnSpacing={5}
                  rowSpacing={4}
                  direction="row"
                  className={classes.Header}
                  alignItems={"center"}
                >
                  <Grid item xs={12}>
                    <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                      <Typography className={classes.HeaderTitle} component="div">
                        Update Password
                      </Typography>
                    </Box>
                    <Box sx={{ display: "inline-flex", ml: 2.5 }}>
                      <ButtonUI
                        className="btn btn-dark"
                        variant="contained"
                        disabled={
                          !(
                            passwords.currPassword.trim() &&
                            passwords.newPassword.trim() &&
                            passwords.confirmPassword.trim()
                          )
                        }
                        onClick={handleUpdatePassword}
                      >
                        Update
                      </ButtonUI>
                    </Box>
                  </Grid>
                </Grid>
                <Box className={classes.Panel} sx={{ mb: 10 }}>
                  <Grid container direction="row">
                    <Grid item xs={12} sm={10} md={6}>
                      {updatePasswordForm()}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </CommonLayout>
  );
}

export default MyProfile;
